import { loadPreference, savePreference } from "../utils";
import { useEffect, useState } from "react";
import { routeDarkRoomLite } from "../../domain/aiRouter";

type DarkRoomPhase = "idle" | "revealing" | "judging" | "complete";

const PERSONA_REVEAL_GROUPS = [
  { axis: "Music", personas: ["The Listener", "The Scribe"] },
  { axis: "Identity", personas: ["The Ghost", "The Vessel"] },
  { axis: "Market", personas: ["The Pulse", "The Hunter"] },
  { axis: "Readiness", personas: ["The Broker", "The Architect"] },
  { axis: "Risk", personas: ["The Heretic", "The Gate"] },
] as const;

const DARK_ROOM_PERSONAS = PERSONA_REVEAL_GROUPS.flatMap((g) => g.personas);

export function DarkRoomStep({ route, onRun, onBack, personaReviews }: {
  route: ReturnType<typeof routeDarkRoomLite>;
  onRun: () => void;
  onBack: () => void;
  personaReviews?: ReadonlyArray<{ persona: string; verdict: string }>;
}) {
  const [phase, setPhase] = useState<DarkRoomPhase>("idle");
  const [revealed, setRevealed] = useState(0);
  const priorRuns = parseInt(loadPreference("shadow-dark-room-runs") ?? "0", 10);
  const fastMode = priorRuns >= 1;
  const revealInterval = fastMode ? 100 : 310;
  const judgingDelay = fastMode ? 600 : 1600;
  const preJudgeDelay = fastMode ? 200 : 400;

  function enterRevealing() {
    savePreference("shadow-dark-room-runs", String(priorRuns + 1));
    setPhase("revealing");
  }

  useEffect(() => {
    if (phase !== "revealing") return;
    if (revealed >= DARK_ROOM_PERSONAS.length) {
      const t = setTimeout(() => setPhase("judging"), preJudgeDelay);
      return () => clearTimeout(t);
    }
    const t = setTimeout(() => setRevealed((r) => r + 1), revealInterval);
    return () => clearTimeout(t);
  }, [phase, revealed, revealInterval, preJudgeDelay]);

  useEffect(() => {
    if (phase !== "judging") return;
    const t = setTimeout(() => { setPhase("complete"); onRun(); }, judgingDelay);
    return () => clearTimeout(t);
  }, [phase, onRun, judgingDelay]);

  useEffect(() => {
    const originalTitle = document.title;
    if (phase === "revealing") {
      const current = Math.min(revealed, DARK_ROOM_PERSONAS.length - 1);
      document.title = `${DARK_ROOM_PERSONAS[current]} · SHADOW`;
    } else if (phase === "judging") {
      document.title = "SHADOW is organizing evidence…";
    } else if (phase === "complete") {
      document.title = "Signal ready · SHADOW";
      return () => { document.title = originalTitle; };
    } else {
      document.title = originalTitle;
    }
    return () => { document.title = originalTitle; };
  }, [phase, revealed]);

  if (phase === "revealing" || phase === "judging" || phase === "complete") {
    const current = Math.min(revealed, DARK_ROOM_PERSONAS.length - 1);
    const allRevealed = revealed >= DARK_ROOM_PERSONAS.length;
    return (
      <section className={`darkRoomCinematic ${phase === "complete" ? "darkRoomComplete" : ""}`} aria-live="polite">
        <div className="cinematicInner">
          <p className="eyebrow">The Dark Room</p>
          {phase === "judging" ? (
            <p className="cinematicStatus judgmentBeat">Organizing supplied evidence…</p>
          ) : (
            <p className="cinematicStatus">
              {allRevealed
                ? "All lenses prepared — compiling Signal..."
                : `Preparing ${DARK_ROOM_PERSONAS[current]} lens...`}
            </p>
          )}
          <div className="personaSequence">
            {PERSONA_REVEAL_GROUPS.flatMap((group) => {
              const groupRevealed = group.personas.filter((_, gi) => {
                const globalIdx = DARK_ROOM_PERSONAS.indexOf(group.personas[gi]);
                return globalIdx < revealed;
              });
              if (groupRevealed.length === 0) return [];
              return [
                <div key={`axis-${group.axis}`} className="axisGroupHeader">{group.axis}</div>,
                ...groupRevealed.map((name) => {
                  const i = DARK_ROOM_PERSONAS.indexOf(name);
                  const verdict = personaReviews?.find((r) => r.persona === name)?.verdict;
                  return (
                    <div key={name} className="personaRevealItem" data-persona={name.toLowerCase().replace(/\s+/g, "-")}>
                      <span>{String(i + 1).padStart(2, "0")}</span>
                      <strong>{name}</strong>
                      <span className="revealVerdict">
                        {verdict ? verdict.split(" ").slice(0, 7).join(" ") + "…" : "signal read"}
                      </span>
                    </div>
                  );
                }),
              ];
            })}
          </div>
          <div className="cinematicProgress">
            <div style={{ width: `${(revealed / DARK_ROOM_PERSONAS.length) * 100}%` }} />
          </div>
          {phase === "revealing" && revealed >= 1 && (
            <button
              type="button"
              className="skipCinematicButton"
              onClick={() => { setRevealed(DARK_ROOM_PERSONAS.length); setPhase("judging"); }}
              style={{ marginTop: "20px", background: "none", border: "none", color: "var(--text-muted)", fontSize: "0.8rem", cursor: "pointer", textDecoration: "underline" }}
            >
              Skip to results
            </button>
          )}
        </div>
      </section>
    );
  }

  return (
    <section className="workspace">
      <article className="panel darkRoomPanel">
        <p className="panelKicker">03 / The Dark Room</p>
        <h2>Ten lenses. One preparation diagnostic.</h2>
        <p>
          {route.allowed
            ? "The Dark Room organizes the text and intake you supply using one local rubric, shown through ten persona lenses. It does not listen to audio or establish expert agreement."
            : route.reason}
        </p>
        <p style={{ color: "var(--text-soft)", fontSize: "0.84rem", marginTop: "8px" }}>
          Mode: {route.mode === "mock" ? "Local preparation; no live provider call" : route.mode.replaceAll("_", " ")}
        </p>
        <div className="formActions splitActions" style={{ marginTop: "28px" }}>
          <button type="button" className="secondaryButton" onClick={onBack}>Back</button>
          <button type="button" onClick={enterRevealing} disabled={!route.allowed}>
            Enter The Dark Room
          </button>
        </div>
      </article>
      <article className="panel">
        <p className="panelKicker">How the diagnostic is organized</p>
        <h2>Ten lenses. One local method.</h2>
        <div className="personaPreview">
          {PERSONA_REVEAL_GROUPS.flatMap((group) => [
            <div key={`axis-${group.axis}`} className="axisGroupHeader">{group.axis}</div>,
            ...group.personas.map((name) => {
              const i = DARK_ROOM_PERSONAS.indexOf(name);
              return (
                <div key={name} className="personaPreviewItem">
                  <span>{String(i + 1).padStart(2, "0")}</span>
                  <strong>{name}</strong>
                </div>
              );
            }),
          ])}
        </div>
      </article>
    </section>
  );
}
